<script setup>
import { ref, onBeforeUnmount, watch } from 'vue';
import { motion } from 'motion-v';
import { AnimatePresence } from 'motion-v';

const props = defineProps({
  title: {
    type: String,
    required: false,
  },
  type: {
    type: String,
    required: false,
    default: "default",
  },
  extra_types: {
    type: Array,
    required: false,
    default: [],
  },
  isOpen: {
    type: Boolean,
    required: false,
    default: false,
  },
  showHeader: {
    type: Boolean,
    required: false,
    default: true,
  },
  showClose: {
    type: Boolean,
    required: false,
    default: true,
  },
  previousModal: {
    type: Function,
    required: false,
    default: null,
  },
});

const emits = defineEmits(["close", "open"]);

const isOpen = ref(props.isOpen);

// props.isOpen 변경 시 내부 ref 동기화 (AnimatePresence가 exit 애니메이션을 감지하도록)
watch(() => props.isOpen, (newValue) => {
  isOpen.value = newValue;
  if (newValue) {
    if (process.client) {
      document.addEventListener('keydown', handleEscapeKey);
    }
  } else {
    if (process.client) {
      document.removeEventListener('keydown', handleEscapeKey);
    }
  }
}, { immediate: true });

function handleEscapeKey(event) {
  if (event.key === 'Escape' || event.keyCode === 27) {
    close();
  }
}

const close = () => {
  // 이전 모달이 있으면 먼저 열기
  if (props.previousModal) {
    props.previousModal();
  }
  
  isOpen.value = false;
  emits('close');
  if (process.client) {
    document.removeEventListener('keydown', handleEscapeKey);
  }
};

const open = () => {
  isOpen.value = true;
  emits('open');
  if (process.client) {
    document.addEventListener('keydown', handleEscapeKey);
  }
};

const isOpened = () => {
  return isOpen.value;
};

onBeforeUnmount(() => {
  if (process.client) {
    document.removeEventListener('keydown', handleEscapeKey);
  }
});

defineExpose({
  close,
  open,
  isOpened,
});
</script>

<template>
  <Teleport to="body">
    <AnimatePresence>
      <div v-if="isOpen" key="modal-wrapper" class="modal" @click="close">
        <motion.div :class="`modal__content modal__type--${props.type} ${props.extra_types.join(' ')}`" :initial="{
          opacity: 0,
          scale: 0.7,
          y: 20
        }" :animate="{
          opacity: 1,
          scale: 1,
          y: 0,
          transition: {
            type: 'spring',
            stiffness: 400,
            damping: 20
          }
        }" :exit="{
            opacity: 0,
            scale: 0.9,
            y: -10,
            transition: {
              duration: 0.15,
              ease: [0.4, 0, 0.2, 1]
            }
          }" @click.stop>
          <div class="modal__content-header" v-if="props.showHeader && $slots.header">
            <slot name="header" :close="close" />
          </div>

          <div class="modal__content-header" v-else-if="props.showHeader && !$slots.header">
            <strong class="modal__content-title">{{ title }}</strong>
            <button type="button" class="modal__content-close ico-close" @click="close" v-if="props.showClose">
              <span class="blind">닫기</span>
            </button>
          </div>

          <slot :close="close" />
        </motion.div>
      </div>
    </AnimatePresence>
  </Teleport>
</template>

<style scoped></style>